/**
 * usePersistentMockState — dual-backed list-state hook.
 *
 * Signature kept stable for backwards compatibility with the 54 call sites
 * across the admin app:
 *
 *   const [rows, setRows] = usePersistentMockState<MockDealer[]>("dealers", seedDealers);
 *
 * Routing (see lib/db/useEntityList for details):
 *
 *   1. If `VITE_INSTANT_APP_ID` is configured AND `storeKey` matches one of
 *      the known InstantDB entities (see lib/db/entityNames.ts), the hook
 *      delegates to InstantDB:
 *        - Reads via db.useQuery → decode BigInts → array
 *        - Writes diff against current → batched db.transact
 *        - Optimistic updates and rollback are handled by the SDK itself
 *        - Offline-cached automatically via IndexedDB (queryCacheLimit=150)
 *
 *   2. Otherwise, it falls back to the legacy localStorage-backed behaviour
 *      via demoStore (BigInt-safe). This is the default until a user
 *      configures their InstantDB app ID.
 *
 * Migration helper: lib/db/migration.ts → seedFromLocalStorage() ports
 * existing localStorage data into InstantDB once on first connect.
 *
 * The hook is intentionally generic on T so existing call sites that pass
 * arrays of various Mock* types remain type-safe. The InstantDB path
 * additionally requires items to have a string `_id` field — every Mock*
 * type in the codebase satisfies this.
 */

import type { Dispatch, SetStateAction } from "react";

import { useEntityList } from "./db/useEntityList";

export function usePersistentMockState<T>(
  storeKey: string,
  initialValue: T,
): [T, Dispatch<SetStateAction<T>>] {
  return useEntityList<T>(storeKey, initialValue);
}
